import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import ApiInspector from '../../../../components/ApiInspector';
import ComboFiltro from '../../../../components/ComboFiltro';
import { apiFetch } from '../../../../lib/api';
import { useFiltroSocieta } from '../../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../../context/PermessiContext';
import { showToast } from '../../../../components/Toast';
import { formatCurrency } from '../../../../utils/formatters';
import { API_DOCS } from './SalPage.api';
import './SalPage.css';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type StatoSal = 'BOZZA' | 'EMESSO' | 'APPROVATO_CLIENTE' | 'APPROVATO_BACKOFFICE' | 'FATTURATO';
type TipoApprovazione = 'CLIENTE' | 'BACKOFFICE';

interface SalItem {
  id: number;
  societaId: number;
  sedeId: number | null;
  commessaId: number | null;
  ordineId: number | null;
  numero: string | null;
  stato: StatoSal;
  dataEmissione: string | null;
  dataApprovazione: string | null;
  tipoApprovazione: TipoApprovazione | null;
  importoTotale: number | string | null;
  tipoDocumento: { id: number; codice: string; nome: string } | null;
  createdAt: string;
  ordine: { id: number; numero: string; flussoCommerciale: string } | null;
  commessa: {
    id: number;
    numero: string;
    descrizione: string | null;
    clienteRagioneSociale: string | null;
  } | null;
  _count: { righe: number };
}

interface CommessaOption {
  id: number;
  numero: string;
  descrizione: string;
  clienteRagioneSociale: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATO_SAL: Record<StatoSal, { label: string; cls: string }> = {
  BOZZA:                { label: 'Bozza',                cls: 'bop-badge-neutral' },
  EMESSO:               { label: 'Emesso',               cls: 'bop-badge-info' },
  APPROVATO_CLIENTE:    { label: 'Approvato cliente',    cls: 'bop-badge-success' },
  APPROVATO_BACKOFFICE: { label: 'Approvato backoffice', cls: 'bop-badge-success' },
  FATTURATO:            { label: 'Fatturato',            cls: 'bop-badge-success' },
};

const TIPO_APPROVAZIONE_LABEL: Record<TipoApprovazione, string> = {
  CLIENTE: 'Cliente',
  BACKOFFICE: 'Backoffice',
};

function formatDateIT(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('it-IT');
}

// Estrae il messaggio dagli errori API ({error: string | {code,message}})
function estraiErrore(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const payload = (err as { payload?: { error?: string | { code?: string; message?: string } } }).payload;
    const e = payload?.error;
    if (e && typeof e === 'object' && e.message) return e.message;
    if (typeof e === 'string' && e) return e;
    if (err instanceof Error && err.message && err.message !== '[object Object]') return err.message;
  }
  return fallback;
}

function renderStato(stato: StatoSal): React.ReactNode {
  const cfg = STATO_SAL[stato] ?? { label: stato, cls: 'bop-badge-neutral' };
  return <span className={`bop-badge ${cfg.cls}`}>{cfg.label}</span>;
}

// ── Pagina ────────────────────────────────────────────────────────────────────

export default function SalPage() {
  const { societaId, listaSocieta, isDisabled, pronta } = useFiltroSocieta();
  const { can, isSuperadmin } = usePermesso();
  const navigate = useNavigate();

  // Stato locale società (solo SUPERADMIN con "Tutte" può modificarlo)
  const [societaIdLocale, setSocietaIdLocale] = useState('');
  const societaIdEff = societaId || societaIdLocale;

  // Filtri
  const [stato, setStato] = useState('');
  const [commessaId, setCommessaId] = useState('');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');

  // Dati
  const [items, setItems] = useState<SalItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [commesseOpts, setCommesseOpts] = useState<CommessaOption[]>([]);

  // Debounce ricerca testo (client-side)
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim().toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  // Carica lista
  const caricaLista = useCallback(async () => {
    if (!pronta) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (societaIdEff) params.set('societaId', societaIdEff);
      if (stato) params.set('stato', stato);
      if (commessaId) params.set('commessaId', commessaId);
      const qs = params.toString() ? `?${params.toString()}` : '';

      // @api GET /api/sal?societaId&stato&commessaId -> {"success":true,"data":[{"id":"number","societaId":"number","sedeId":"number","commessaId":"number","ordineId":"number","numero":"string|null","stato":"BOZZA|EMESSO|APPROVATO_CLIENTE|APPROVATO_BACKOFFICE|FATTURATO","dataEmissione":"string","dataApprovazione":"string","tipoApprovazione":"CLIENTE|BACKOFFICE","importoTotale":"number","tipoDocumento":{"id":"number","codice":"string","nome":"string"},"createdAt":"string","ordine":{"id":"number","numero":"string","flussoCommerciale":"string"},"commessa":{"id":"number","numero":"string","descrizione":"string","clienteRagioneSociale":"string"},"_count":{"righe":"number"}}]}
      const r = await apiFetch<{ success: boolean; data: SalItem[] }>(`/api/sal${qs}`);
      setItems(r.data || []);
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nel caricamento dei SAL'), 'error');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [pronta, societaIdEff, stato, commessaId]);

  useEffect(() => { caricaLista(); }, [caricaLista]);

  // Carica commesse per il combo (cambia con societaId effettiva)
  useEffect(() => {
    if (!societaIdEff) {
      setCommesseOpts([]);
      return;
    }
    // @api GET /api/commesse?societaId={id} -> {"commesse":[{"id":"number","numero":"string","descrizione":"string","clienteRagioneSociale":"string"}]}
    apiFetch<{ commesse: CommessaOption[] }>(`/api/commesse?societaId=${societaIdEff}`)
      .then(r => setCommesseOpts(r.commesse || []))
      .catch(() => setCommesseOpts([]));
  }, [societaIdEff]);

  // Resetta commessa se cambia società
  useEffect(() => { setCommessaId(''); }, [societaIdEff]);

  const commesseComboItems = commesseOpts.map(c => ({
    id: String(c.id),
    nome: `${c.numero} — ${c.descrizione || ''}`.trim(),
  }));

  // Ricerca client-side su numero, ordine, commessa, cliente
  const itemsFiltrati = useMemo(() => {
    if (!q) return items;
    return items.filter(s => {
      const testo = [
        s.numero || '',
        s.ordine?.numero || '',
        s.commessa?.numero || '',
        s.commessa?.descrizione || '',
        s.commessa?.clienteRagioneSociale || '',
      ].join(' ').toLowerCase();
      return testo.includes(q);
    });
  }, [items, q]);

  const pulisciFiltri = () => {
    setStato('');
    setCommessaId('');
    setQInput('');
    setQ('');
  };

  const renderApprovazione = (s: SalItem): React.ReactNode => {
    if (!s.tipoApprovazione && !s.dataApprovazione) return <span className="bop-text-muted">—</span>;
    return (
      <div className="sal-lista__approvazione">
        {s.tipoApprovazione && (
          <span className="sal-lista__approvazione-tipo">{TIPO_APPROVAZIONE_LABEL[s.tipoApprovazione]}</span>
        )}
        {s.dataApprovazione && (
          <span className="bop-text-muted">{formatDateIT(s.dataApprovazione)}</span>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div className="sal-lista">
        {/* Header — i SAL si creano da qui scegliendo la commessa (o dalla scheda ordine) */}
        <div className="bop-page-header">
          <h1 className="bop-page-title">SAL</h1>
          {can('commerciale.sal', 'edit') && (
            <button
              className="bop-btn bop-btn-primary"
              onClick={() => navigate('/commerciale/attivo/sal/nuovo')}
            >
              + Nuovo SAL
            </button>
          )}
        </div>

        {/* Filtri */}
        <div className="bop-filter-bar sal-lista__filters-row">
          {!isDisabled && (
            <div className="bop-form-group">
              <label className="bop-label">Società</label>
              <select
                className="bop-select"
                value={societaIdLocale}
                onChange={e => setSocietaIdLocale(e.target.value)}
              >
                {isSuperadmin && <option value="">Tutte</option>}
                {listaSocieta.map(s => (
                  <option key={s.id} value={String(s.id)}>{s.nome}</option>
                ))}
              </select>
            </div>
          )}

          <div className="bop-form-group">
            <label className="bop-label">Cerca</label>
            <input
              className="bop-input sal-lista__search-input"
              value={qInput}
              onChange={e => setQInput(e.target.value)}
              placeholder="Numero, ordine, commessa, cliente..."
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Stato</label>
            <select
              className="bop-select"
              value={stato}
              onChange={e => setStato(e.target.value)}
            >
              <option value="">Tutti</option>
              <option value="BOZZA">Bozza</option>
              <option value="EMESSO">Emesso</option>
              <option value="APPROVATO_CLIENTE">Approvato cliente</option>
              <option value="APPROVATO_BACKOFFICE">Approvato backoffice</option>
              <option value="FATTURATO">Fatturato</option>
            </select>
          </div>

          <div className="bop-form-group sal-lista__combo-group">
            <label className="bop-label">Commessa</label>
            <ComboFiltro
              label="commessa"
              value={commessaId || null}
              onChange={id => setCommessaId(id || '')}
              items={commesseComboItems}
              disabled={!societaIdEff}
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">&nbsp;</label>
            <button className="bop-btn bop-btn-secondary" onClick={pulisciFiltri}>
              Pulisci filtri
            </button>
          </div>
        </div>

        {/* Tabella */}
        {!pronta ? (
          <div className="bop-card">
            <div className="bop-empty-state">Seleziona una società per visualizzare i SAL</div>
          </div>
        ) : (
          <div className="bop-card">
            <table className="bop-table bop-table--clickable">
              <thead>
                <tr>
                  <th>Numero</th>
                  <th>Tipo</th>
                  <th>Ordine</th>
                  <th>Commessa</th>
                  <th className="bop-text-right">Righe</th>
                  <th className="bop-text-right">Importo</th>
                  <th>Stato</th>
                  <th>Data emissione</th>
                  <th>Approvazione</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9}>
                      <div className="bop-empty-state">Caricamento…</div>
                    </td>
                  </tr>
                ) : itemsFiltrati.length === 0 ? (
                  <tr>
                    <td colSpan={9}>
                      <div className="bop-empty-state">
                        <p className="bop-empty-state__text">
                          Nessun SAL trovato con i filtri attuali.
                          <br />
                          I SAL si creano da qui scegliendo la commessa, o dalla scheda ordine.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  itemsFiltrati.map(s => (
                    <tr key={s.id} onClick={() => navigate(`/commerciale/attivo/sal/${s.id}`)}>
                      <td>
                        {s.numero
                          ? <span className="sal-lista__numero">{s.numero}</span>
                          : <span className="bop-badge bop-badge-neutral">Bozza</span>}
                      </td>
                      <td className="sal-lista__tipo-doc">
                        {s.tipoDocumento ? s.tipoDocumento.codice : <span className="bop-text-muted">—</span>}
                      </td>
                      <td>
                        {s.ordine
                          ? s.ordine.numero
                          : <span className="bop-badge bop-badge-neutral">Più ordini</span>}
                      </td>
                      <td>
                        {s.commessa ? (
                          <div className="sal-lista__commessa">
                            <span className="sal-lista__commessa-numero">{s.commessa.numero}</span>
                            {s.commessa.clienteRagioneSociale && (
                              <span className="bop-text-muted"> — {s.commessa.clienteRagioneSociale}</span>
                            )}
                          </div>
                        ) : (
                          <span className="bop-text-muted">—</span>
                        )}
                      </td>
                      <td className="bop-text-right">{s._count?.righe ?? 0}</td>
                      <td className="bop-text-right">{formatCurrency(Number(s.importoTotale ?? 0))}</td>
                      <td>{renderStato(s.stato)}</td>
                      <td>{formatDateIT(s.dataEmissione)}</td>
                      <td>{renderApprovazione(s)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
